import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { getAuditIdFromParams } from '@cdab/scania/qpr/loaders'
import type { DOSAuditReportData } from '@cdab/scania/qpr/schema'
import { useTranslation } from 'react-i18next'
import type { LoaderFunctionArgs } from 'react-router'
import { isRouteErrorResponse, json, useRouteError } from 'react-router'

export type PrintLoaderData = {
  auditData: DOSAuditReportData
}

export async function loader({
  params
}: LoaderFunctionArgs): Promise<PrintLoaderData> {
  const auditId = getAuditIdFromParams(params)

  if (!auditId) {
    throw json({ status: 404, message: 'Audit ID is required' })
  }

  try {
    const client = getClient()
    const auditData = await client.ReportsService.GetDOSReportData(auditId)
    return { auditData }
  } catch (error) {
    if (error instanceof Response) {
      throw error
    }
    throw json({ status: 500, message: 'Failed to load audit data' })
  }
}

export const formatDate = (dateString: string) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString()
}

export function ErrorBoundary() {
  const error = useRouteError()
  const { t } = useTranslation('common')

  if (isRouteErrorResponse(error)) {
    return (
      <html lang='en'>
        <head>
          <meta charSet='UTF-8' />
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0'
          />
          <title>Error</title>
        </head>
        <body>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h1>{t('error')}</h1>
            <p>{error.data?.message || 'Something went wrong'}</p>
          </div>
        </body>
      </html>
    )
  }

  return (
    <html lang='en'>
      <head>
        <meta charSet='UTF-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
        <title>Error</title>
      </head>
      <body>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>{t('error')}</h1>
          <p>{t('something-went-wrong')}</p>
        </div>
      </body>
    </html>
  )
}
